import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useApi } from '../api/context';
import { LiveStream, type LiveStatus } from '../api/sse';
import type { LiveEventType } from '../api/types';

/**
 * The signed-in panel's live connection to GET /events/stream. Pages subscribe to the event types
 * they care about (payment.received, qr_intent.updated, resync) and refetch; nothing here keeps
 * data of its own.
 */
type Listener = (type: string, data: unknown) => void;

interface LiveContextValue {
  status: LiveStatus;
  subscribe: (listener: Listener) => () => void;
}

const LiveContext = createContext<LiveContextValue | null>(null);

export function LiveProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [status, setStatus] = useState<LiveStatus>('idle');
  const listeners = useRef(new Set<Listener>());

  useEffect(() => {
    const stream = new LiveStream({
      url: api.eventsUrl(),
      getToken: () => api.accessToken(),
      refresh: () => api.refresh(),
      fetchImpl: api.fetchImpl,
      onStatus: setStatus,
      onEvent: (type, data) => {
        for (const l of [...listeners.current]) {
          try {
            l(type, data);
          } catch {
            // one page's handler must not break the others
          }
        }
      },
    });
    stream.start();
    const wake = (): void => {
      if (document.visibilityState === 'visible') stream.reconnectNow();
    };
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', wake);
    return () => {
      window.removeEventListener('online', wake);
      document.removeEventListener('visibilitychange', wake);
      stream.stop();
    };
  }, [api]);

  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const value = useMemo<LiveContextValue>(() => ({ status, subscribe }), [status, subscribe]);

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

/** 'idle' outside a LiveProvider (tests, the research dashboard). */
export function useLiveStatus(): LiveStatus {
  return useContext(LiveContext)?.status ?? 'idle';
}

/**
 * Calls `handler` for every event of `types` (the latest handler, without re-subscribing). A no-op
 * outside a LiveProvider.
 */
export function useLiveEvents(
  types: readonly LiveEventType[],
  handler: (type: LiveEventType, data: unknown) => void,
): void {
  const ctx = useContext(LiveContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const key = types.join(',');
  const subscribe = ctx?.subscribe;

  useEffect(() => {
    if (!subscribe) return undefined;
    const wanted = new Set(key.split(','));
    return subscribe((type, data) => {
      if (wanted.has(type)) handlerRef.current(type as LiveEventType, data);
    });
  }, [subscribe, key]);
}
